import { useState } from 'react';
import {
  Film,
  Loader2,
  AlertCircle,
  Plus,
  Check,
  X,
  Trash2,
  ImageIcon,
  Video,
  MessageSquareQuote,
} from 'lucide-react';
import { MediaType } from '@pms/shared';
import type { ClientMedia } from '@pms/shared';
import { usePlatformProperties } from '@/hooks/api/usePlatform';
import {
  useClientMedia,
  useCreateClientMedia,
  useDeleteClientMedia,
  useApproveClientMedia,
  useRejectClientMedia,
} from '@/hooks/api/useContent';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { extractErrorMessage } from '@/lib/error';
import { cn } from '@/lib/utils';

const MEDIA_ICON: Record<MediaType, typeof ImageIcon> = {
  [MediaType.IMAGE]: ImageIcon,
  [MediaType.VIDEO]: Video,
  [MediaType.REVIEW]: MessageSquareQuote,
};

// ── Add form ────────────────────────────────────────────────────────────────

function AddMediaForm({ propertyId }: { propertyId: string }) {
  const [mediaType, setMediaType] = useState<MediaType>(MediaType.IMAGE);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateClientMedia(propertyId);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    create.mutate(
      {
        mediaType,
        url: url.trim() || undefined,
        title: title.trim() || undefined,
        body: body.trim() || undefined,
      },
      {
        onSuccess: () => {
          setUrl('');
          setTitle('');
          setBody('');
        },
        onError: (err) => setError(extractErrorMessage(err)),
      },
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
    >
      <div className="flex flex-wrap gap-3">
        <div className="min-w-[140px]">
          <label htmlFor="m-type" className="block text-xs font-semibold text-gray-500 mb-1">
            Type
          </label>
          <select
            id="m-type"
            value={mediaType}
            onChange={(e) => setMediaType(e.target.value as MediaType)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm capitalize focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            {Object.values(MediaType).map((t) => (
              <option key={t} value={t} className="capitalize">
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label htmlFor="m-url" className="block text-xs font-semibold text-gray-500 mb-1">
            URL {mediaType === MediaType.REVIEW ? '(optional)' : ''}
          </label>
          <input
            id="m-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label htmlFor="m-title" className="block text-xs font-semibold text-gray-500 mb-1">
            Title (optional)
          </label>
          <input
            id="m-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
      </div>
      <div>
        <label htmlFor="m-body" className="block text-xs font-semibold text-gray-500 mb-1">
          Body / testimonial (optional)
        </label>
        <textarea
          id="m-body"
          rows={2}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? (
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
          ) : (
            <Plus size={16} aria-hidden="true" />
          )}
          Add media
        </Button>
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    </form>
  );
}

// ── Media row ───────────────────────────────────────────────────────────────

function MediaRow({ media, propertyId }: { media: ClientMedia; propertyId: string }) {
  const approve = useApproveClientMedia(propertyId);
  const reject = useRejectClientMedia(propertyId);
  const remove = useDeleteClientMedia(propertyId);
  const Icon = MEDIA_ICON[media.mediaType];

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-5 py-4">
        <span className="inline-flex items-center gap-1.5 text-sm text-gray-700 capitalize">
          <Icon size={16} className="text-gray-400" aria-hidden="true" />
          {media.mediaType}
        </span>
      </td>
      <td className="px-5 py-4 text-sm text-gray-700">
        <div className="font-medium">{media.title || <span className="text-gray-400">—</span>}</div>
        {media.body && <div className="text-xs text-gray-500 line-clamp-2 max-w-md">{media.body}</div>}
        {media.url && (
          <a
            href={media.url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-brand-700 hover:underline break-all"
          >
            {media.url}
          </a>
        )}
      </td>
      <td className="px-5 py-4">
        <span
          className={cn(
            'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
            media.isApproved ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
          )}
        >
          {media.isApproved ? 'Approved' : 'Pending'}
        </span>
      </td>
      <td className="px-5 py-4">
        <div className="flex items-center justify-end gap-1">
          {!media.isApproved ? (
            <button
              type="button"
              aria-label="Approve"
              disabled={approve.isPending}
              onClick={() => approve.mutate(media.id)}
              className="rounded-md p-1 text-gray-400 hover:bg-green-50 hover:text-green-600"
            >
              <Check size={16} aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              aria-label="Reject"
              disabled={reject.isPending}
              onClick={() => reject.mutate(media.id)}
              className="rounded-md p-1 text-gray-400 hover:bg-amber-50 hover:text-amber-600"
            >
              <X size={16} aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            aria-label="Delete"
            disabled={remove.isPending}
            onClick={() => remove.mutate(media.id)}
            className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 size={16} aria-hidden="true" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export function AdminMediaPage() {
  const { data: properties, isLoading: propsLoading } = usePlatformProperties();
  const [propertyId, setPropertyId] = useState<string>('');
  const { data: media, isLoading, isError, refetch } = useClientMedia(propertyId || null);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Media Moderation"
        description="Moderate operator-supplied images, videos, and testimonials per property."
      />

      <div className="max-w-md">
        <label htmlFor="media-property" className="block text-sm font-semibold text-gray-900 mb-1.5">
          Property
        </label>
        <select
          id="media-property"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          disabled={propsLoading}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">Select a property…</option>
          {(properties ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {p.city}
            </option>
          ))}
        </select>
      </div>

      {!propertyId ? (
        <EmptyState
          icon={Film}
          title="No property selected"
          description="Pick a property above to manage its media."
        />
      ) : isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-brand-600" aria-hidden="true" />
        </div>
      ) : isError ? (
        <div className="py-16 text-center">
          <AlertCircle size={24} className="mx-auto text-red-500 mb-3" aria-hidden="true" />
          <p className="text-sm text-red-700 mb-4">Unable to load media.</p>
          <Button variant="secondary" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <div className="space-y-5">
          <AddMediaForm propertyId={propertyId} />
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {(media ?? []).length === 0 ? (
              <EmptyState
                icon={Film}
                title="No media yet"
                description="Add media using the form above."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead>
                    <tr className="bg-gray-50">
                      {['Type', 'Content', 'Status', ''].map((c, i) => (
                        <th
                          key={c || `c-${i}`}
                          scope="col"
                          className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                        >
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(media ?? []).map((m) => (
                      <MediaRow key={m.id} media={m} propertyId={propertyId} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
